import { useTranslation } from 'react-i18next';
import { Input, TextArea } from 'components/core';
import { isAtCharLimit } from 'helpers';

import styles from './DashboardTabs.module.css';
import { FieldTooltip } from 'molecules/fieldTooltip/FieldTooltip';

interface InfoProps {
  register: any;
  setValue: any;
  formState: any;
  isExisting?: boolean;
  /** Offer the Live/Test status field. Only when the API reports a status, so it is never a control that does nothing. */
  showStatus?: boolean;
  titleMaxLength: number;
  descriptionMaxLength: number;
  titleValue: string;
  descriptionValue: string;
}

export const Info = ({
  register,
  setValue,
  formState,
  isExisting = false,
  showStatus = false,
  titleMaxLength,
  descriptionMaxLength,
  titleValue,
  descriptionValue,
}: InfoProps) => {
  const { t } = useTranslation();
  const { errors } = formState;
  const isTitleAtLimit = isAtCharLimit(titleValue, titleMaxLength);
  const isDescriptionAtLimit = isAtCharLimit(descriptionValue, descriptionMaxLength);

  const handleChange =
    (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setValue(field, e.target.value, { shouldValidate: true });
    };

  return (
    <div className="col-sm-12 col-md-12 col-lg-6 mt-4 mb-3 h-100">
      {/* Title */}
      <div className="mb-4">
        <div className={styles.fieldLabelRow}>
          <span className={styles.fieldLabel}>Title</span>
          <FieldTooltip
            title="Dashboard title"
            description="The name that appears at the top of the dashboard when users open it in the MIS Web App. Keep it short and descriptive so users immediately know what data they are looking at."
            example="Pre Service Dashboard"
          />
        </div>
        <Input
          type="text"
          showDescription={false}
          showTitle={false}
          placeholder="Type here"
          maxLength={titleMaxLength}
          {...register('title')}
          className={`${errors.title || isTitleAtLimit ? 'is-invalid' : ''}`}
          onChange={handleChange('title')}
        />
        <div className="invalid-feedback">{errors.title?.message}</div>
        {!errors.title && isTitleAtLimit && (
          <div className={styles.fieldLimitWarning}>{t('VALIDATION.MAX_LENGTH', { n: titleMaxLength })}</div>
        )}
      </div>

      {/* Description */}
      <div className="mb-4">
        <div className={styles.fieldLabelRow}>
          <span className={styles.fieldLabel}>
            Description <span className={styles.optional}>(Optional)</span>
          </span>
          <FieldTooltip
            title="Description"
            description="A plain English explanation of what this dashboard shows and who it is for. This helps admins understand the purpose of the dashboard. It is not shown to end users."
            example="Registration and user engagement data for Pre-Service nursing and midwifery students across Bihar."
          />
        </div>
        <TextArea
          rows={5}
          showTitle={false}
          showDescription={false}
          title=""
          maxLength={descriptionMaxLength}
          {...register('description')}
          className={`${errors.description || isDescriptionAtLimit ? 'is-invalid' : ''}`}
          onChange={handleChange('description')}
        />
        <div className="invalid-feedback">{errors.description?.message}</div>
        {!errors.description && isDescriptionAtLimit && (
          <div className={styles.fieldLimitWarning}>{t('VALIDATION.MAX_LENGTH', { n: descriptionMaxLength })}</div>
        )}
      </div>

      {/* Dashboard ID */}
      <div className="mb-4">
        <div className={styles.fieldLabelRow}>
          <span className={styles.fieldLabel}>Dashboard ID</span>
          <div className="invalid-feedback">{errors.dashboardId?.message}</div>
          <FieldTooltip
            title="Dashboard ID"
            description="A unique internal identifier for this dashboard. It is used by the system to link roles, widgets and filters to this specific dashboard. Once set and saved, do not change it — changing it will break all existing role and widget mappings."
            example="aastrikaDashboards"
            note="Use camelCase with no spaces. Changing this after creation will break existing configurations."
          />
        </div>
        <Input
          type="text"
          showDescription={false}
          showTitle={false}
          placeholder="Type here"
          {...register('dashboardId')}
          className={`${errors.dashboardId ? 'is-invalid' : ''}`}
          onChange={handleChange('dashboardId')}
          disabled={isExisting}
          style={
            isExisting
              ? { opacity: 0.6, cursor: 'not-allowed', background: 'var(--bg-surface-secondary)' }
              : {}
          }
        />
        <div className="invalid-feedback">{errors.dashboardId?.message}</div>
      </div>

      {/* Status: only for a saved dashboard whose API reports one. A new dashboard gets the environment's default. */}
      {showStatus && (
        <div className="mb-4">
          <div className={styles.fieldLabelRow}>
            <span className={styles.fieldLabel}>{t('DASHBOARD_STATUS.FIELD_LABEL')}</span>
            <FieldTooltip title={t('DASHBOARD_STATUS.FIELD_TITLE')} description={t('DASHBOARD_STATUS.FIELD_HELP')} />
          </div>
          <select className={`form-select ${styles.statusSelect}`} aria-label={t('DASHBOARD_STATUS.FIELD_LABEL')} {...register('status')}>
            <option value="LIVE">{t('DASHBOARD_STATUS.LIVE')}</option>
            <option value="TEST">{t('DASHBOARD_STATUS.TEST')}</option>
          </select>
        </div>
      )}
    </div>
  );
};
